import React, { useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { HeadProvider, Link } from "react-head";
import { Asset, Flex, utils } from "@thenounproject/lingo-core";
import ThumbnailWrapper from "./GalleryAssetContent/GalleryAssetThumbnail/ThumbnailWrapper";
import useResizeObserver from "@components/hooks/useResizeObserver";

type ThumbnailProps = {
  asset: Asset;
  fontSize?: string;
  displayAsName?: boolean;
};

type TextSampleProps = {
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  fontSize?: string;
};

const TextSamplePlain = styled(Flex).attrs<TextSampleProps>(props => {
  return {
    color: "black",
    style: {
      fontFamily: props.fontFamily,
      fontSize: "18px",
      fontWeight: props.fontWeight || "normal",
      fontStyle: props.fontStyle || "normal",
    },
  };
})<TextSampleProps>`
  ${utils.ellipsis}
`;

const TextSample = styled(Flex).attrs<TextSampleProps>(props => {
  return {
    color: "black",
    style: {
      fontFamily: props.fontFamily,
      fontSize: props.fontSize,
      fontWeight: props.fontWeight || "normal",
      fontStyle: props.fontStyle || "normal",
    },
  };
})<TextSampleProps>`
  text-align: center;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background-color: ${utils.getColor("grayLight")};
  border-radius: 4px;
`;

function FontThumbnail({ asset, fontSize, displayAsName }: ThumbnailProps) {
  const [loaded, setLoaded] = useState(false);
  const [isSmall, setIsSmall] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback((node: Element) => {
    setIsSmall(node.clientWidth < 100);
  }, []);

  useResizeObserver(wrapperRef, handleResize);

  const fontMeta = asset.meta.font;
  const { name, family, googleVariant, source, stylesheetUrl } = fontMeta;

  function getTextSampleProps(): TextSampleProps {
    const computedFontSize = isSmall ? "48px" : fontSize || "64px";
    if (source === "google-fonts" && googleVariant) {
      const [_, weight, style] = googleVariant.match(/^([0-9]*)([a-z]*)$/);
      return {
        fontFamily: family,
        fontWeight: weight,
        fontStyle: style,
        fontSize: computedFontSize,
      };
    }
    return {
      fontFamily: name,
      fontSize: computedFontSize,
    };
  }

  return (
    <HeadProvider>
      <Link href={stylesheetUrl} rel="stylesheet" onLoad={() => setLoaded(true)} />
      {displayAsName ? (
        <TextSamplePlain {...getTextSampleProps()}>{fontMeta.displayName}</TextSamplePlain>
      ) : (
        <ThumbnailWrapper ref={wrapperRef}>
          {loaded ? (
            <TextSample {...getTextSampleProps()}>Aa</TextSample>
          ) : (
            <TextSample fontSize={isSmall ? "48px" : "64px"}>Aa</TextSample>
          )}
        </ThumbnailWrapper>
      )}
    </HeadProvider>
  );
}

export default React.memo(FontThumbnail);
